<template>
  <section class="auth-login">
    <div class="container d-flex justify-content-center">
      <div class="col-12 col-sm-10 col-md-6">
        <BlocksCard>
          <h2 class="__heading">
            Login
          </h2>

          <transition name="fade">
            <!-- Registration Form -->
            <div v-if="!loginResponse || loginResponse === 401" :key="`registration-form`" class="auth-login__form" @keydown.enter.prevent="login">
              <div class="row auth-login__form">
                <div class="col-12 auth-login__form-email auth-login__form-block">
                  <ElementsInputsText v-model="email" v-model:is-valid="isValid" label="Email" type="text" :rules="{fieldName: 'Email', rules: {required: true, email: true}}" variant="underlined" />
                </div>
                <div class="col-12 auth-login__form-block">
                  <ElementsInputsText v-model="password" v-model:is-valid="isValid" label="Password" type="password" :rules="{fieldName: 'Password', rules: {required: true}}" variant="underlined" />
                </div>
                <div class="col-md-12">
                  <ElementsRecaptcha ref="recaptchaRef" />
                </div>
                <div class="col-12">
                  <nuxt-link class="btn-text-link btn-forgot-password" to="/auth/forgot-password">
                    Forgot password?
                  </nuxt-link>
                </div>
              </div>
              <div class="row __action">
                <div class="col-12 auth-login__form-block __action-button">
                  <ElementsButtonsRegular label="Login" @click="login" />
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <nuxt-link class="btn-text-link btn-login" to="/auth/register">
                    I don't have an account? Register
                  </nuxt-link>
                </div>
              </div>
              <div class="row auth-login__alternate-login-options">
                <div class="col-12">
                  <div class="line-text">
                    <span>OR</span>
                  </div>
                </div>
                <div class="col-12 auth-login__button-wrapper">
                  <AuthLoginFacebook v-model:loading="isLoading" v-model:response="loginResponse" />
                  <AuthLoginGoogle v-model:loading="isLoading" v-model:response="loginResponse" />
                </div>
              </div>

              <div v-if="isLoading" class="loading-wrapper">
                <div class="d-flex justify-content-center">
                  <div class="auth-login__loading-spinner">
                    <img v-lazy-load  :data-src="spinner" alt="Loading">
                  </div>
                </div>
              </div>
            </div>

            <!-- Login Response -->
            <div v-else :key="`registration-response`" class="auth-login__response">
              <div class="row">
                <div class="auth-login__response-content">
                  <p v-if="loginResponse && [403, 407, 500].includes(loginResponse)">
                    Oops! Something went wrong. please try again later.
                  </p>
                </div>
              </div>
            </div>
          </transition>
        </BlocksCard>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {Ref} from 'vue';
import spinner from '~/assets/images/icons/dynamic-spinner.svg';
import {useRouter} from '#imports';

const email: Ref<string | null> = ref(null);
const password: Ref<string | null> = ref(null);
const isValid: Ref<string[]> = ref([]);
const isLoading: Ref<boolean> = ref(false);
const {$login} = useNuxtApp();
const loginResponse: Ref<200 | 201 | 403 | 406 | 407 | 500 | null> = ref(null);
const router = useRouter();
const recaptchaRef: Ref<HTMLElement | null> = ref(null);

/**
 * Login
 */
function login() {
  // Return of the form is loading
  if (isLoading.value || !recaptchaRef.value) {
    return;
  }

  // Run validating by changing the value (fields have a watch on them and those will run the validation when isValid is changed)
  isValid.value = ['validate'];
  nextTick(async () => {
    if (!(isValid.value.filter((item) => item !== 'validate').length)) {
      isLoading.value = true;

      // generate recaptcha token
      const token = await (recaptchaRef.value as any).validate();
      const response = await $login({
        email: email.value as string,
        password: password.value,
        token,
      });

      if (response.code === 200) {
        router.push('/auth/my-account');
      }
      isLoading.value = false;
    }
  });
}
</script>

<style lang="scss">
.auth-login {
  &__form {
    margin-top: 28px;

    &-block {
      margin-bottom: 28px;
      display: flex;
      justify-content: center;
    }
  }

  .btn-text-link {
    font-size: 14px;
    color: var(--primary);
    text-decoration: underline;
    display: block;
    padding-right: 12px;

    &.btn-forgot-password {
      text-align: right;
    }

    &.btn-login {
      margin-top: 20px;
      text-align: center;
    }
  }

  .auth-login__alternate-login-options {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-top: 40px;

    .line-text {
      text-align: center;
      overflow: hidden;
      white-space: nowrap;
      background: #fff;
      display: flex;
      justify-content: center;

      span {
        background: #fff;
        z-index: 2;
        padding: 10px 20px; /* You may adjust this value */
        position: relative;
        width: fit-content;
      }

      &:before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        display: inline-block;
        width: 100%;
        height: 1px;
        background: #d5d5d5;
        z-index: 1;
      }
    }

    .auth-login__button-wrapper {
      margin-top: 20px;
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
    }
  }

  .loading-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    background: rgba(255, 255, 255, 0.3);
    width: 100%;
    height: 100%;
    z-index: 2;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__loading-spinner {
    margin: 12px;
    width: 32px;
    height: 32px;
  }

  &__response {
    &-content {
      strong {
        margin-right: 5px;
        color: green;
      }
      padding: 12px 12px;
      margin: 0 auto;
    }
  }
}
</style>
